"use client";

import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import Image from "next/image";
import Modal from "@/components/custom-modal";
import { IconButton } from "@/components/icon-button";

const navBtnStyle = "z-10 top-1/2 -translate-y-1/2 bg-white hover:bg-blue-50 border-1 border-brand-700 shadow-sm"

export const MediaSlider = ({ media }: any) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  const handlePrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setActiveIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setActiveIndex((prev) => Math.min(media.length - 1, prev + 1));
  };

  const handleThumbnailClick = (index: number) => {
    setActiveIndex(index);
  };

  const handleImageClick = (index: number) => {
    if (media[index].type === "image") {
      setModalIndex(index);
      setModalOpen(true);
    }
  };

  const handleModalClose = () => setModalOpen(false);

  const handleModalPrev = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (modalIndex === 0) return;
    const nextIndex = modalIndex - 1;
    setModalIndex(nextIndex);
    setActiveIndex(nextIndex);
  };

  const handleModalNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (modalIndex === media.length - 1) return;
    const nextIndex = modalIndex + 1;
    setModalIndex(nextIndex);
    setActiveIndex(nextIndex);
  };

  const isImage = media[activeIndex]?.type === "image";
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === media.length - 1;
  const isModalFirst = modalIndex === 0;
  const isModalLast = modalIndex === media.length - 1;

  return (
    <div className="w-full flex flex-col justify-between relative my-0 md:my-2 h-auto md:h-134">
      {/* Main View — container div so nav IconButtons are not nested inside a button */}
      <div
        className="w-full flex justify-center items-center bg-[#F9FAFB] rounded-2xl relative overflow-hidden transition-transform duration-300 ease-in-out h-65 sm:h-85 md:h-115"
        style={{
          cursor: isImage ? "pointer" : "default",
        }}
        onMouseEnter={(e) => {
          if (isImage) {
            e.currentTarget.style.transform = "scale(1.01)";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        {isImage && (
          <>
            <Image
              src={media[activeIndex]?.url || "/assets/imagePlaceholder.png"}
              alt={`Property Media ${activeIndex}`}
              fill
              sizes="(max-width: 768px) 100vw, 640px"
              priority={activeIndex === 0}
              className="object-cover rounded-2xl"
              placeholder="blur"
              blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88f7TfwAJBAP6X6u3VAAAAABJRU5ErkJggg=="
            />
            <button
              type="button"
              aria-label="باز کردن تصویر"
              className="absolute inset-0 z-[1] cursor-pointer bg-transparent"
              onClick={() => handleImageClick(activeIndex)}
            />
          </>
        )}

        {/* Navigation Arrows */}
        {media.length > 1 && (
          <>
            {!isLast && (
              <IconButton
                onClick={handleNext}
                className={`absolute left-1 md:left-2.5 ${navBtnStyle}`}
              >
                <ChevronLeft color="#3538CD" size={24} strokeWidth={2.5}/>
              </IconButton>
            )}

            {!isFirst && (
              <IconButton
                onClick={handlePrev}
                className={`absolute right-1 md:right-2.5 ${navBtnStyle}`}
              >
                <ChevronRight color="#3538CD" size={24} strokeWidth={2.5}/>
              </IconButton>
            )}
          </>
        )}
      </div>

      {/* Thumbnails */}
      <div className="flex gap-1 overflow-auto py-1 max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {media.map((item: any, index: number) => (
          <button
            type="button"
            key={item.id}
            className={`relative shrink-0 w-15 sm:w-17.5 md:w-20 h-11.25 sm:h-13.75 md:h-15 rounded-lg overflow-hidden transition-all duration-200 ease-in-out cursor-pointer ${activeIndex === index
              ? "border-2 border-brand-700"
              : "border border-section"
              }`}
            onClick={() => handleThumbnailClick(index)}
          >
            <Image
              src={item.url || "/assets/imagePlaceholder.png"}
              alt="thumbnail"
              fill
              className="object-cover"
              sizes="80px"
            />
          </button>
        ))}
      </div>

      {/* Fullscreen Image Modal */}
      <Modal isOpen={modalOpen} onClose={handleModalClose} animate={false}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black w-screen h-screen flex items-center justify-center outline-none">
          <IconButton
            onClick={handleModalClose}
            className="absolute top-5 right-5 text-white z-10"
          >
            <X size={32} />
          </IconButton>

          {media.length > 1 && (
            <>
              {!isModalLast && (
                <IconButton
                  onClick={handleModalNext}
                  className={`absolute left-3 md:left-6 ${navBtnStyle}`}
                  ariaLabel="تصویر بعدی"
                >
                  <ChevronLeft color="#3538CD" strokeWidth={3} size={24} />
                </IconButton>
              )}

              {!isModalFirst && (
                <IconButton
                  onClick={handleModalPrev}
                  className={`absolute right-3 md:right-6 ${navBtnStyle}`}
                  ariaLabel="تصویر قبلی"
                >
                  <ChevronRight color="#3538CD" strokeWidth={3} size={24} />
                </IconButton>
              )}
            </>
          )}

          <div className="relative w-full h-full max-w-5xl max-h-[90vh]">
            <Image
              src={media[modalIndex]?.url || "/assets/imagePlaceholder.png"}
              alt="Fullscreen"
              fill
              sizes="100vw"
              className="object-contain"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};